import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBooking } from '../../../../api/bookingApi';
import '../css/Modal.css';

const AddBookingModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    customer: {
      customerName: '',
      phoneNumber: '',
      email: ''
    },
    numGuests: 1,
    tableIds: [],
    bookingAt:'',
    note: ''
  });

  // Separate state for first and last name inputs
  const [nameInputs, setNameInputs] = useState({
    firstName: '',
    lastName: ''
  });

  
  const [validationErrors, setValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleNameChange = (field, value) => {
    const newNameInputs = {
      ...nameInputs,
      [field]: value
    };
    setNameInputs(newNameInputs);

    // Update customerName in formData
    const fullName = `${newNameInputs.firstName} ${newNameInputs.lastName}`.trim();
    handleInputChange('customer.customerName', fullName);

    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };


  const validateForm = () => {
    const errors = [];
    
    if (!nameInputs.firstName.trim()) {
      errors.push(t('admin.customer.add_modal.validation.first_name'));
    }
    
    if (!nameInputs.lastName.trim()) {
      errors.push(t('admin.customer.add_modal.validation.last_name'));
    }
    
    if (!formData.customer.phoneNumber.trim()) {
      errors.push(t('admin.customer.add_modal.validation.phone'));
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.customer.phoneNumber)) {
      errors.push(t('admin.customer.add_modal.validation.phone_format'));
    }
    
    if (!formData.customer.email.trim()) {
      errors.push(t('admin.customer.add_modal.validation.email'));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer.email)) {
      errors.push(t('admin.customer.add_modal.validation.email_format'));
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await createBooking(formData);
      onSuccess(response);
      onClose();
    } catch (error) {
      console.error('Error creating customer:', error);
      setValidationErrors([t('admin.customer.add_modal.validation.create_failed')]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderValidationErrors = () => {
    if (validationErrors.length === 0) return null;

    return (
      <div className="validation-errors">
        <h4>{t('admin.base_modal.validation_title')}</h4>
        <ul>
          {validationErrors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>{t('admin.customer.add_modal.title')}</h2>
            <button className="close-btn" onClick={onClose} aria-label={t('admin.base_modal.close')}>×</button>
          </div>
          
          <div className="modal-body">
            {renderValidationErrors()}
            
            <div className="edit-section">
              <h3>{t('admin.customer.add_modal.customer_info')}</h3>
              <div className="form-grid">
                <div className="form-item">
                  <label>{t('admin.customer.add_modal.first_name')} *:</label>
                  <input
                    type="text"
                    value={nameInputs.firstName}
                    onChange={(e) => handleNameChange('firstName', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.customer.add_modal.placeholders.first_name')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.customer.add_modal.last_name')} *:</label>
                  <input
                    type="text"
                    value={nameInputs.lastName}
                    onChange={(e) => handleNameChange('lastName', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.customer.add_modal.placeholders.last_name')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.customer.add_modal.phone')} *:</label>
                  <input
                    type="text"
                    value={formData.customer.phoneNumber}
                    onChange={(e) => handleInputChange('customer.phoneNumber', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.customer.add_modal.placeholders.phone')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.customer.add_modal.email')} *:</label>
                  <input
                    type="email"
                    value={formData.customer.email}
                    onChange={(e) => handleInputChange('customer.email', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.customer.add_modal.placeholders.email')}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('admin.customer.add_modal.buttons.cancel')}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('admin.customer.add_modal.buttons.creating') : t('admin.customer.add_modal.buttons.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBookingModal;